import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Gift, Smartphone, Store, Rocket, ChevronDown, ChevronUp, PartyPopper, Tablet } from 'lucide-react';
import { baseApi } from '../../api/baseClient';
import { useAuth } from '../../context/AuthContext';

// Checklist de activación (F1c del plan de conversión). Reemplaza al carrusel
// OnboardingInstructions: en vez de explicar, marca en automático lo ya hecho
// y deja visible el camino a los 10 clientes (la meta que mueve la retención).
// Datos desde lealify-base (GET /analytics/dashboard → activation).
// Plan: docs/lealify-wizard-onboarding-plan.md §4

const TARGET = 10;

interface DashboardActivation {
  show: boolean;
  has_program: boolean;
  has_rewards: boolean;
  has_contacts: boolean;
  has_checkins: boolean;
  whatsapp_connected: boolean;
  contacts: number;
}
interface DashboardResponse {
  activation: DashboardActivation;
}

const ActivationChecklist: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasReward, setHasReward] = useState(false);
  const [clients, setClients] = useState(currentUser?.registeredClients || 0);
  const [kioskDone, setKioskDone] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const track = (event: string) => { (window as any).gtag?.('event', event); };
  // "Kiosco en el mostrador" no tiene señal en base: lo confirma el dueño (lo
  // persistimos por navegador) o se da por hecho si ya hubo check-ins reales.
  const kioskKey = currentUser?.businessId ? `lealify_kiosk_done_${currentUser.businessId}` : '';

  useEffect(() => {
    const load = async () => {
      if (!currentUser?.businessId) return;
      try {
        const { activation } = await baseApi.get<DashboardResponse>('/analytics/dashboard');
        setHasReward(!!activation.has_rewards);
        setClients(activation.contacts);
        const localKiosk = kioskKey ? localStorage.getItem(kioskKey) === '1' : false;
        setKioskDone(!!activation.has_checkins || localKiosk);

        // Hitos GA4 una sola vez por negocio (persistido en localStorage)
        const fireOnce = (k: string): boolean => {
          const key = `lealify_milestone_${currentUser.businessId}_${k}`;
          if (localStorage.getItem(key)) return false;
          localStorage.setItem(key, '1');
          return true;
        };
        if (activation.contacts >= 1 && fireOnce('first')) track('first_client');
        if (activation.contacts >= TARGET && fireOnce('tenth')) track('tenth_client');
      } catch (e) {
        console.error('[ActivationChecklist] load:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser?.businessId]);

  const markKiosk = () => {
    setKioskDone(true);
    if (kioskKey) localStorage.setItem(kioskKey, '1');
  };

  if (loading || !currentUser || currentUser.role === 'admin') return null;

  const firstClient = clients >= 1;
  const tenth = clients >= TARGET;
  const progressPct = Math.min(Math.round((clients / TARGET) * 100), 100);
  const doneCount = [hasReward, firstClient, kioskDone, tenth].filter(Boolean).length;

  // 🎉 Meta lograda: celebración única y se despide
  if (tenth) {
    return (
      <div className="bg-surface rounded-lg shadow-md p-6 text-center animate-fade-in-up border-2 border-green-300 dark:border-green-500/40">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 dark:bg-green-500/10 mb-3">
          <PartyPopper className="h-7 w-7 text-green-600" />
        </div>
        <h3 className="text-lg font-bold text-content mb-1">¡Lo lograste! {clients >= TARGET ? '10+' : clients} clientes registrados 🎉</h3>
        <p className="text-sm text-content-2 mb-4">Tu programa de lealtad ya camina solo. Sigue registrando a cada cliente que pague y mira crecer tus visitas recurrentes.</p>
        <button onClick={onDismiss} className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-colors">
          <Check className="inline h-4 w-4 mr-1" /> Entendido
        </button>
      </div>
    );
  }

  const Item: React.FC<{ done: boolean; icon: React.ReactNode; title: string; sub: React.ReactNode; action?: React.ReactNode }> =
    ({ done, icon, title, sub, action }) => (
      <div className={`flex items-start p-3 rounded-lg border ${done ? 'border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10' : 'border-line bg-surface'}`}>
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${done ? 'bg-green-500 text-white' : 'bg-surface-2 text-content-3'}`}>
          {done ? <Check className="h-4 w-4" /> : icon}
        </div>
        <div className="flex-grow min-w-0">
          <div className={`text-sm font-semibold ${done ? 'text-green-700 dark:text-green-300 line-through' : 'text-content'}`}>{title}</div>
          {!done && <div className="text-xs text-content-2 mt-0.5">{sub}</div>}
        </div>
        {!done && action && <div className="flex-shrink-0 ml-2">{action}</div>}
      </div>
    );

  return (
    <div data-tour="activation-checklist" className="bg-surface rounded-lg shadow-md overflow-hidden animate-fade-in-up">
      <div className="px-5 pt-4 pb-3 flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-content flex items-center">
            <Rocket className="h-4 w-4 mr-2 text-accent" /> Pon a andar tu programa
          </h3>
          <p className="text-xs text-content-3 mt-0.5">{doneCount} de 4 completados — al llegar a 10 clientes, esto desaparece solo.</p>
        </div>
        <button onClick={onDismiss} className="text-xs text-content-3 hover:text-content-2">Ocultar</button>
      </div>

      <div className="px-5 pb-4 space-y-2">
        <Item done={hasReward} icon={<Gift className="h-4 w-4" />} title="Crea tu primera recompensa"
          sub={<>El premio por el que tus clientes regresan.</>}
          action={<Link to="/dashboard/rewards" className="text-xs font-semibold text-accent hover:text-accent/75">Crear →</Link>} />

        <Item done={firstClient} icon={<Smartphone className="h-4 w-4" />} title="Regístrate tú como tu primer cliente"
          sub={<>Abre el kiosco, teclea tu celular y vívelo como tus clientes.</>}
          action={<a href="https://app.lealify.com" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-accent hover:text-accent/75">Abrir kiosco →</a>} />

        <Item done={kioskDone} icon={<Store className="h-4 w-4" />} title="Deja el kiosco en tu mostrador"
          sub={<>En la tablet, celular o computadora de tu caja (<a href="https://app.lealify.com" target="_blank" rel="noopener noreferrer" className="text-accent">app.lealify.com</a>, mismo correo y contraseña).</>}
          action={<button onClick={markKiosk} className="text-xs font-semibold text-accent hover:text-accent/75 whitespace-nowrap">Ya está ✓</button>} />

        {/* Meta de 10 clientes con barra de progreso */}
        <div className="p-3 rounded-lg border border-line">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center text-sm font-semibold text-content">
              <Tablet className="h-4 w-4 mr-2 text-content-3" /> Tus primeros 10 clientes
            </div>
            <span className="text-sm font-bold text-accent">{clients} / {TARGET}</span>
          </div>
          <div className="w-full bg-skeleton rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
          <p className="mt-1.5 text-xs text-content-3">La meta que marca la diferencia: registra a cada cliente que pague.</p>
        </div>

        {/* Siguientes pasos opcionales (no urgentes) */}
        <button onClick={() => setShowMore(v => !v)} className="w-full flex items-center justify-center text-xs text-content-3 hover:text-content-2 pt-1">
          Personaliza tu negocio {showMore ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
        </button>
        {showMore && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <Link to="/dashboard/business" className="p-2.5 rounded-lg border border-line hover:border-blue-400 text-content-2 hover:text-blue-600 text-center transition-colors">🎨 Logo y colores</Link>
            <Link to="/dashboard/mensajes?evento=welcome" className="p-2.5 rounded-lg border border-line hover:border-blue-400 text-content-2 hover:text-blue-600 text-center transition-colors">💬 Mensaje de bienvenida</Link>
            <Link to="/dashboard/business?tab=kiosk" className="p-2.5 rounded-lg border border-line hover:border-blue-400 text-content-2 hover:text-blue-600 text-center transition-colors">📋 Datos que pides al cliente</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivationChecklist;
